import { TypedDocumentNode } from '@apollo/client';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetTicketQuery as GetTicket } from '__generated__/graphql';

import { getServerErrors } from '../../../../common/helpers';
import GetEventLayoutQuery from '../GetEventLayoutQuery';
import UI from '../../../../common/UI';
import useEditTicketMutation from './useEditTicketMutation';
import useIntegerInput from '../../../../common/useIntegerInput';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';

type Event = GetTicket['event'];
type Ticket = GetTicket['ticket'];

export interface EditTicketTypeFormProps {
  ticket: Ticket;
  event: Event;
  refetchQueries?: TypedDocumentNode[];
}

const EditTicketTypeForm = ({
  ticket, event, refetchQueries = [GetEventLayoutQuery],
}: EditTicketTypeFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const integerInput = useIntegerInput();

  const getValues = (ticket: Ticket) => ({
    business: ticket.business,
    preregistration: ticket.preregistration,
    units: ticket.units,
  });

  const form = useForm({
    values: getValues(ticket),
  });

  useNavigationPrompt(form.changed());

  const [groupTicketMode, setGroupTicketMode] = useState(ticket.units > 1 && !ticket.business);

  const [editTicket, { error, loading }] = useEditTicketMutation({
    input: {
      id: ticket.id,
      type: {
        ...form.values,
        units: form.values.units || 1,
      },
    },
    onSuccess: (ticket) => {
      // Reset group ticket mode because units = 1 means single ticket
      setGroupTicketMode(ticket.units > 1 && !ticket.business);
      form.reset(getValues(ticket));
    },
    refetchQueries,
  });

  const setSingleTicket = () => {
    setGroupTicketMode(false);
    form.set((values) => ({ ...values, business: false, preregistration: false, units: 1 }));
  };

  const setGroupTicket = () => {
    setGroupTicketMode(true);
    form.set((values) => ({
      ...values,
      business: false,
      preregistration: false,
      units: values.units === 1 ? null : values.units,
    }));
  };

  const setBusinessTicket = () => {
    setGroupTicketMode(false);
    form.set((values) => ({ ...values, business: true, preregistration: false }));
  };

  const setPreregistration = () => {
    setGroupTicketMode(false);
    form.set((values) => ({ ...values, business: false, preregistration: true, units: 1 }));
  };

  const errors = getServerErrors(error, 'type');

  return (
    <UI.Form onSubmit={() => editTicket()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('ticket_type')}
          </UI.Legend>
          <UI.InputDescription>
            {t('ticket_type_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.FormGrid sc={{ columns: '1fr', gutter: 0.25 }}>
          <UI.Radio
            onChange={() => setSingleTicket()}
            checked={!groupTicketMode && !form.values.business && !form.values.preregistration}
            sc={{ box: true }}
            style={{ flex: 1 }}
          >
            <UI.Strong>{t('single_ticket')}</UI.Strong>
            <UI.Div>{t('single_ticket_description')}</UI.Div>
          </UI.Radio>
          <UI.Radio
            onChange={() => setGroupTicket()}
            checked={groupTicketMode && !form.values.business}
            sc={{ box: true }}
            style={{ flex: 1 }}
          >
            <UI.Strong>{t('group_ticket')}</UI.Strong>
            <UI.Div>{t('group_ticket_description')}</UI.Div>
          </UI.Radio>
          <UI.Radio
            onChange={() => setBusinessTicket()}
            checked={form.values.business}
            sc={{ box: true }}
            style={{ flex: 1 }}
          >
            <UI.Strong>{t('business_ticket')}</UI.Strong>
            <UI.Div>{t('business_ticket_description')}</UI.Div>
          </UI.Radio>
          <UI.Radio
            onChange={() => setPreregistration()}
            checked={!groupTicketMode && !form.values.business && form.values.preregistration}
            sc={{ box: true }}
            style={{ flex: 1 }}
          >
            <UI.Strong>{t('preregistration_ticket')}</UI.Strong>
            <UI.Div>{t('preregistration_ticket_description')}</UI.Div>
          </UI.Radio>
        </UI.FormGrid>

        {(groupTicketMode || form.values.business) && (
          <UI.InputGroup sc={{ valid: !errors.units, touched: form.touched.units }}>
            <UI.InputLabel htmlFor="group_size">
              {t('group_size')}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('group_size_description')}
            </UI.InputDescription>
            <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
              <UI.Div>
                <UI.DebouncedInput
                  {...form.text('units', { inputHandler: integerInput() })}
                  format={integerInput().format}
                  id="group_size"
                  maxLength={3}
                  inputMode="numeric"
                  match={/[\d]/g}
                  placeholder="1"
                />
                <UI.ErrorMessages attribute={t('group_size')} errors={errors.units} />
              </UI.Div>
            </UI.FormGrid>
          </UI.InputGroup>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_ticket')}
          closeTo={`/${project.id}/events/${event.id}/tickets`}
          loading={loading}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditTicketTypeForm;
